import { z } from 'zod';
import { LocalSchema, EventoStatusSchema } from './evento';

/**
 * Candidatura + filtros + responses for empresa/profissional surfaces.
 *
 * Single source of truth shared by backend DTOs (createZodDto) and frontend api-client.
 * Framework-agnostic — duplicates enum literals (does NOT import @prisma/client).
 *
 * Status enum mirrors Prisma `CandidaturaStatus`.
 * Funcao mirrors Prisma `Funcao`.
 * TipoContratacao mirrors Prisma `TipoContratacao` (re-imported via evento.ts).
 */

// ---------- Funcao ----------

export const FuncaoSchema = z.enum(['MONTADOR', 'TECNICO', 'CARREGADOR']);

// ---------- Status enums ----------

export const CandidaturaStatusSchema = z.enum([
  'pendente',
  'aprovada',
  'rejeitada',
  'concluida',
  'cancelada',
  'cancelada_empresa',
]);

// UI filter for /candidaturas/me — labels that map onto internal enum:
//   confirmados → aprovada
//   concluidos  → concluida
//   cancelados  → [rejeitada, cancelada]
//   pendente    → pendente
export const CandidaturaStatusFiltroProfSchema = z
  .enum(['pendente', 'confirmados', 'concluidos', 'cancelados'])
  .optional();

// Empresa filter — direct map to internal enum.
// Phase 10 v1.1 — added cancelada + cancelada_empresa for the empresa candidaturas
// list filter chips (see CONTEXT.md "Empresa lista de candidaturas").
export const CandidaturaStatusFiltroEmpresaSchema = z
  .enum(['pendente', 'aprovada', 'rejeitada', 'cancelada', 'cancelada_empresa'])
  .optional();

// ---------- Inputs ----------

export const CandidatarInputSchema = z.object({
  funcaoAplicada: FuncaoSchema,
  mensagem: z.string().max(500).optional(),
});

export const RejeitarInputSchema = z.object({
  motivo: z.string().max(200).optional(),
});

// Phase 10 v1.1 — CANC-01 input. motivo is optional free text (200-char cap mirrors
// RejeitarInput to share the same UI textarea component). Trimmed on submit.
export const CancelarCandidaturaSchema = z.object({
  motivo: z.string().trim().max(200).optional(),
});

/**
 * Dispensa individual de candidato aprovado pela empresa (antecedência mínima 24h).
 * Motivo obrigatório (min 5, max 500) para transparência e LGPD privacy-by-design.
 */
export const DispensarCandidaturaSchema = z.object({
  motivo: z
    .string()
    .trim()
    .min(5, 'Informe o motivo com pelo menos 5 caracteres')
    .max(500, 'Motivo máximo 500 caracteres'),
});

// ---------- Phase 30 — Bulk Operations + CSV Export ----------

/**
 * Bulk approve: send an array of candidatura IDs to approve atomically.
 * min(1) ensures at least one ID; max(200) caps payload size.
 */
export const BulkAprovarInputSchema = z.object({
  candidaturaIds: z.array(z.string().cuid()).min(1).max(200),
});

/**
 * Bulk reject: same ID array constraints as BulkAprovarInputSchema;
 * optional motivo mirrors the existing RejeitarInputSchema constraint (max 200).
 */
export const BulkRejeitarInputSchema = z.object({
  candidaturaIds: z.array(z.string().cuid()).min(1).max(200),
  motivo: z.string().trim().max(200).optional(),
});

/**
 * Response for both bulk-approve-selected, aprovar-todos-pendentes, and bulk-reject.
 * processados = number of rows mutated; candidaturaIds = the IDs that were changed.
 * min(0) on processados allows the no-op case (e.g., zero pendentes remaining).
 */
export const BulkOperacaoResponseSchema = z.object({
  processados: z.number().int().min(0),
  candidaturaIds: z.array(z.string()),
  ignoradosConflito: z.number().int().min(0).optional(),
});

/**
 * 409 payload returned when bulk-approve would exceed vaga.quantidade.
 * erro is a literal discriminator; count_atual = current approved count;
 * count_disponivel = remaining slots at the time of the request.
 */
export const BulkOverApprovalErrorSchema = z.object({
  erro: z.literal('over_approval'),
  count_atual: z.number().int().min(0),
  count_disponivel: z.number().int().min(0),
});

/**
 * Query-param contract for GET /eventos/:id/candidatos/export.
 * Reuses CandidaturaStatusFiltroEmpresaSchema which is already .optional(),
 * so omitting status exports all statuses.
 */
export const CsvExportQuerySchema = z.object({
  status: CandidaturaStatusFiltroEmpresaSchema,
});

// ---------- Candidatura DTO (profissional view) ----------

// Inline subsets used by Candidatura responses.
const CandidaturaEventoSubsetSchema = z.object({
  id: z.string(),
  nome: z.string(),
  dataInicio: z.string(),
  dataFim: z.string(),
  local: LocalSchema,
  tipoContratacao: z.enum(['ABERTO', 'SELECAO']),
  status: EventoStatusSchema,
  empresaNome: z.string().nullable().optional(),
  empresaLogoUrl: z.string().url().nullable().optional(),
});

const CandidaturaVagaSubsetSchema = z.object({
  id: z.string(),
  funcao: FuncaoSchema,
  valorCents: z.number().int(),
  quantidade: z.number().int(),
});

export const CandidaturaDtoSchema = z.object({
  id: z.string(),
  vagaId: z.string(),
  profissionalId: z.string(),
  eventoId: z.string(),
  funcaoAplicada: FuncaoSchema,
  status: CandidaturaStatusSchema,
  mensagem: z.string().nullable(),
  motivoRejeicao: z.string().nullable(),
  criadaEm: z.string(),
  decididaEm: z.string().nullable(),
  // Phase 10 v1.1 — cancellation audit trail.
  canceladaEm: z.string().nullable(),
  motivoCancelamento: z.string().nullable(),
  evento: CandidaturaEventoSubsetSchema,
  vaga: CandidaturaVagaSubsetSchema,
});

export const MeusEventosResponseSchema = z.object({
  candidaturas: z.array(CandidaturaDtoSchema),
  total: z.number().int(),
});

// ---------- Candidato item (empresa view) ----------

export const CandidatoItemSchema = z.object({
  id: z.string(),
  profissionalId: z.string(),
  nomeCompleto: z.string(),
  telefone: z.string(),
  email: z.string(),
  verificado: z.boolean(),
  /** Phase 11 (MEDIA-02): canonical public URL of profissional avatar; null when unset. */
  avatarUrl: z.string().url().nullable(),
  funcaoAplicada: FuncaoSchema,
  status: CandidaturaStatusSchema,
  mensagem: z.string().nullable(),
  motivoRejeicao: z.string().nullable(),
  canceladaEm: z.string().nullable().optional(),
  motivoCancelamento: z.string().nullable().optional(),
  criadaEm: z.string(),
  decididaEm: z.string().nullable(),
});

export const CandidatosPorFuncaoSchema = z.object({
  funcao: FuncaoSchema,
  quantidadeVaga: z.number().int(),
  aprovados: z.number().int(),
  candidatos: z.array(CandidatoItemSchema),
});

export const CandidatosResponseSchema = z.object({
  eventoId: z.string(),
  totalVagas: z.number().int(),
  totalCandidatos: z.number().int(),
  totalAprovados: z.number().int(),
  totalPendentes: z.number().int(),
  totalRejeitados: z.number().int(),
  candidatosPorFuncao: z.array(CandidatosPorFuncaoSchema),
});

// ---------- CandidaturaAudit (Phase 12 v1.2 — AUDIT-CAND-01) ----------

// One audit row per changed field on a Candidatura mutation. Mirrors the
// EventoAudit DTO shape from evento.ts — `candidaturaId` replaces `eventoId`.
// `actorNome` + `actorPapel` are joined server-side; null when the user has
// been deleted (LGPD right-to-be-forgotten) OR for system-driven mutations
// (promoteFromQueue emits actorId=null because no human acted).
export const CandidaturaAuditDtoSchema = z.object({
  id: z.string(),
  candidaturaId: z.string(),
  actorId: z.string().nullable(),
  actorNome: z.string().nullable(),
  actorPapel: z.enum(['ADMINISTRADOR', 'COLABORADOR', 'VISUALIZADOR']).nullable(),
  field: z.string(),
  oldValue: z.unknown(),
  newValue: z.unknown(),
  at: z.string(),
});

export const CandidaturaAuditListResponseSchema = z.object({
  rows: z.array(CandidaturaAuditDtoSchema),
  nextCursor: z.string().nullable(),
});

// ---------- Fila (Phase 17 v1.3 — FILA-01) ----------

/**
 * One row in the empresa-facing fila list. `reputacao` is null when the
 * profissional has zero avaliacoes (total=0) — frontend renders a "—".
 * `avatarUrl` mirrors the CandidatoItem field shape (canonical public URL or
 * null).
 */
export const FilaRowSchema = z.object({
  candidaturaId: z.string(),
  profissional: z.object({
    id: z.string(),
    nomeCompleto: z.string(),
    avatarUrl: z.string().url().nullable(),
    reputacao: z.number().nullable(),
  }),
  criadoEm: z.string(),
  posicao: z.number().int().min(1),
});

export const FilaListResponseSchema = z.object({
  rows: z.array(FilaRowSchema),
  nextCursor: z.string().nullable(),
});

/**
 * One row in the profissional-facing "my fila" widget/page. Returned as a flat
 * array (no pagination at MVP — a single profissional rarely has > 10 fila
 * positions; if needed v1.4 can paginate).
 */
export const MyPendenteRowSchema = z.object({
  candidaturaId: z.string(),
  eventoId: z.string(),
  eventoNome: z.string(),
  vagaId: z.string(),
  funcao: FuncaoSchema,
  dataInicio: z.string(),
  posicao: z.number().int().min(1),
  totalNaFila: z.number().int().min(1),
});

export const MyPendentesResponseSchema = z.array(MyPendenteRowSchema);

// ---------- Inferred types ----------
// Note: Funcao + CandidaturaStatus types are NOT re-exported here — they live in
// ../enums (canonical value+type). Schemas inferred types are structurally identical,
// so consumers import `Funcao` / `CandidaturaStatus` from the package root (enums).

export type CandidaturaStatusFiltroProf = z.infer<
  typeof CandidaturaStatusFiltroProfSchema
>;
export type CandidaturaStatusFiltroEmpresa = z.infer<
  typeof CandidaturaStatusFiltroEmpresaSchema
>;
export type CandidatarInput = z.infer<typeof CandidatarInputSchema>;
export type RejeitarInput = z.infer<typeof RejeitarInputSchema>;
export type CancelarCandidaturaInput = z.infer<typeof CancelarCandidaturaSchema>;
export type DispensarCandidaturaInput = z.infer<typeof DispensarCandidaturaSchema>;
export type CandidaturaDto = z.infer<typeof CandidaturaDtoSchema>;
export type MeusEventosResponse = z.infer<typeof MeusEventosResponseSchema>;
export type CandidatoItem = z.infer<typeof CandidatoItemSchema>;
export type CandidatosPorFuncao = z.infer<typeof CandidatosPorFuncaoSchema>;
export type CandidatosResponse = z.infer<typeof CandidatosResponseSchema>;
export type CandidaturaAuditDto = z.infer<typeof CandidaturaAuditDtoSchema>;
export type CandidaturaAuditListResponse = z.infer<
  typeof CandidaturaAuditListResponseSchema
>;
export type FilaRow = z.infer<typeof FilaRowSchema>;
export type FilaListResponse = z.infer<typeof FilaListResponseSchema>;
export type MyPendenteRow = z.infer<typeof MyPendenteRowSchema>;
export type MyPendentesResponse = z.infer<typeof MyPendentesResponseSchema>;

// Phase 30 — Bulk Operations + CSV Export
export type BulkAprovarInput = z.infer<typeof BulkAprovarInputSchema>;
export type BulkRejeitarInput = z.infer<typeof BulkRejeitarInputSchema>;
export type BulkOperacaoResponse = z.infer<typeof BulkOperacaoResponseSchema>;
export type BulkOverApprovalError = z.infer<typeof BulkOverApprovalErrorSchema>;
export type CsvExportQuery = z.infer<typeof CsvExportQuerySchema>;

// ---------- Phase 32 — Cursor pagination (DEBT-04) ----------

/**
 * Query-param contract for GET /eventos/:id/candidatos/page.
 * cursor: opaque HMAC-signed token from a previous response; absent on first page.
 * limit: coerced from query string; defaults to 20; clamped to 100 server-side.
 * status: optional empresa-facing status filter.
 */
export const CandidatosPageQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: CandidaturaStatusFiltroEmpresaSchema.optional(),
});

/**
 * Response shape for GET /eventos/:id/candidatos/page.
 * items: flat array of CandidatoItem (not grouped by funcao — use /candidatos for that).
 * nextCursor: opaque token to pass as ?cursor= on the next request; null on last page.
 */
export const CandidatosPageSchema = z.object({
  items: z.array(CandidatoItemSchema),
  nextCursor: z.string().nullable(),
});

export type CandidatosPageQuery = z.infer<typeof CandidatosPageQuerySchema>;
export type CandidatosPage = z.infer<typeof CandidatosPageSchema>;
